import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { ArrowLeft, CreditCard, Check } from 'lucide-react';

const OnboardingBilling = ({ data, onNext, onPrev, loading }) => {
  const [selectedPlan, setSelectedPlan] = useState(data?.selected_plan || 'basic');
  const [billingAddress, setBillingAddress] = useState({
    same_as_company: data?.billing_address?.same_as_company ?? true,
    street: data?.billing_address?.street || '',
    city: data?.billing_address?.city || '',
    state: data?.billing_address?.state || '',
    zip: data?.billing_address?.zip || '',
    country: data?.billing_address?.country || 'US',
  });

  const plans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      price: 29,
      features: [
        'Up to 5 projects',
        'Basic email support',
        '10GB storage',
        'Standard features',
      ],
      popular: false,
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: 99,
      features: [
        'Unlimited projects',
        'Priority support',
        '100GB storage',
        'Advanced features',
        'API access',
      ],
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise Plan',
      price: 299,
      features: [
        'Unlimited everything',
        'Dedicated support',
        '1TB storage',
        'Custom integrations',
        'SLA guarantee',
      ],
      popular: false,
    },
  ];

  const handleBillingAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const billingData = {
      selected_plan: selectedPlan,
      billing_address: billingAddress,
    };

    onNext(billingData);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">
          Select a plan that best fits your needs. You can upgrade or downgrade anytime.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Plan Selection */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative border rounded-lg p-6 cursor-pointer transition-all ${
                selectedPlan === plan.id
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                
                <ul className="space-y-2 text-sm text-gray-600">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {selectedPlan === plan.id && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Billing Address */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Billing Address</h3>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="same_as_company"
              name="same_as_company"
              checked={billingAddress.same_as_company}
              onChange={handleBillingAddressChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="same_as_company" className="ml-2 text-sm text-gray-700">
              Same as company address
            </label>
          </div>

          {!billingAddress.same_as_company && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <Input
                  type="text"
                  name="street"
                  value={billingAddress.street}
                  onChange={handleBillingAddressChange}
                  placeholder="Enter street address"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <Input
                    type="text"
                    name="city"
                    value={billingAddress.city}
                    onChange={handleBillingAddressChange}
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <Input
                    type="text"
                    name="state"
                    value={billingAddress.state}
                    onChange={handleBillingAddressChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <Input
                    type="text"
                    name="zip"
                    value={billingAddress.zip}
                    onChange={handleBillingAddressChange}
                    placeholder="Enter ZIP code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <select
                    name="country"
                    value={billingAddress.country}
                    onChange={handleBillingAddressChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Billing Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-2">Billing Summary</h4>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">
              {plans.find(p => p.id === selectedPlan)?.name}
            </span>
            <span className="font-medium">
              ${plans.find(p => p.id === selectedPlan)?.price}/month
            </span>
          </div>
          <div className="border-t pt-2">
            <div className="flex justify-between items-center font-medium">
              <span>Total</span>
              <span>${plans.find(p => p.id === selectedPlan)?.price}/month</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            You'll be charged monthly starting from your next billing cycle.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onPrev}
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingBilling;